import type {
  SkillTreeResponse,
  SkillBlobResponse,
  ReprocessRequest,
  ReprocessResponse,
} from "@contracts/contract-5-github-proxy";
import type { GitHubSource } from "@contracts/contract-1-sqs-envelope";
import { getApiToken } from "./auth";
import { env, IS_DEV } from "./env";
import { reportError } from "./newrelic";

export type { SkillTreeResponse, SkillBlobResponse, ReprocessRequest, ReprocessResponse };

/**
 * Derive the legacy install-key format (`source--org--repo[--skill-path]`) that
 * github-proxy's `parseInstallKey` decodes.
 *
 * The web UI's MarketplaceEntry has `source`, `orgOrOwner`, `repoName`, and
 * `skillPath` as separate fields — but github-proxy's download / delete / files
 * endpoints still take a single encoded string. Issue #176 / Path #1 changed
 * the marketplace-published `installName` to be the raw `plugin.json.name`
 * (which github-proxy can't parse), so the web UI now derives this legacy key
 * locally for github-proxy calls only. The user-facing install snippet still
 * uses `plugin.installName` directly.
 *
 * Long-term: github-proxy should accept `{source, org, repo, skillPath}` as
 * structured fields and the legacy parse can be retired.
 */
export function legacyInstallKey(plugin: {
  source: GitHubSource;
  orgOrOwner: string;
  repoName: string;
  skillPath?: string;
}): string {
  const base = `${plugin.source}--${plugin.orgOrOwner.toLowerCase()}--${plugin.repoName.toLowerCase()}`;
  if (!plugin.skillPath) return base;
  return `${base}--${plugin.skillPath.toLowerCase().replace(/\//g, "--")}`;
}

interface PresignResponse {
  uploadUrl: string;
  stagingKey: string;
}

interface UploadResponse {
  commitSha: string;
  repoUrl: string;
  installName: string;
}

class GitHubProxyError extends Error {
  constructor(
    message: string,
    public statusCode: number,
  ) {
    super(message);
    this.name = "GitHubProxyError";
  }
}

async function getProxyToken(): Promise<string> {
  const token = await getApiToken();
  if (token === null) throw new GitHubProxyError("Not authenticated", 401);
  return token;
}

async function authHeaders(): Promise<Record<string, string>> {
  if (IS_DEV) {
    return { "Content-Type": "application/json" };
  }
  const token = await getProxyToken();
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

function baseUrl(): string {
  if (IS_DEV) return "";
  const url = env.REACT_APP_GITHUB_PROXY_URL;
  if (!url) return "";
  return url.replace(/\/$/, "");
}

async function handleResponse<T>(response: Response): Promise<T> {
  let body: Record<string, unknown>;
  try {
    body = await response.json();
  } catch {
    throw new GitHubProxyError(
      `Server returned non-JSON response (${response.status})`,
      response.status,
    );
  }
  if (!response.ok) {
    throw new GitHubProxyError(
      (body.error as string) || `Request failed (${response.status})`,
      response.status,
    );
  }
  return body as T;
}

export async function getPresignedDownloadUrl(
  installKey: string,
  skillPath?: string,
  skillName?: string,
): Promise<string> {
  const headers = await authHeaders();
  const body: Record<string, string> = { installKey };
  if (skillPath) body.skillPath = skillPath;
  if (skillName) body.skillName = skillName;

  const response = await fetch(`${baseUrl()}/skill/download`, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
  });
  const result = await handleResponse<{ downloadUrl: string }>(response);
  return result.downloadUrl;
}

export async function presignUpload(
  fileName: string,
  contentType: string,
  stagingKeyPrefix?: string,
): Promise<PresignResponse> {
  const headers = await authHeaders();
  const response = await fetch(`${baseUrl()}/skill/presign`, {
    method: "POST",
    headers,
    body: JSON.stringify({ fileName, contentType, stagingKeyPrefix }),
  });
  return handleResponse<PresignResponse>(response);
}

function rewriteS3Url(url: string): string {
  if (!IS_DEV) return url;
  try {
    const parsed = new URL(url);
    return `/dev-s3${parsed.pathname}${parsed.search}`;
  } catch {
    return url;
  }
}

export async function uploadFileToS3(
  presignedUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(rewriteS3Url(presignedUrl), {
    method: "PUT",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
  if (!response.ok) {
    throw new GitHubProxyError(`S3 upload failed (${response.status})`, response.status);
  }
}

export async function publishSkill(request: {
  stagingKeyPrefix: string;
  skillName: string;
  description: string;
  keywords: string[];
  contributorEmail: string;
  contributorName: string;
  previousInstallKey?: string;
}): Promise<UploadResponse> {
  const headers = await authHeaders();
  const response = await fetch(`${baseUrl()}/skill`, {
    method: "PUT",
    headers,
    body: JSON.stringify(request),
  });
  return handleResponse<UploadResponse>(response);
}

export async function fetchSkillTree(
  installKey: string,
  commitSha: string,
  skillPath?: string,
): Promise<SkillTreeResponse> {
  const params = new URLSearchParams({ installKey, commitSha });
  if (skillPath) params.set("skillPath", skillPath);
  const response = await fetch(`${baseUrl()}/skill/files?${params.toString()}`, {
    headers: await authHeaders(),
  });
  return handleResponse<SkillTreeResponse>(response);
}

export async function fetchSkillBlob(
  installKey: string,
  blobSha: string,
): Promise<SkillBlobResponse> {
  const params = new URLSearchParams({ installKey, blobSha });
  const response = await fetch(`${baseUrl()}/skill/files?${params.toString()}`, {
    headers: await authHeaders(),
  });
  return handleResponse<SkillBlobResponse>(response);
}

export async function deleteSkill(installKey: string): Promise<void> {
  const headers = await authHeaders();
  const response = await fetch(
    `${baseUrl()}/skill?installKey=${encodeURIComponent(installKey)}`,
    { method: "DELETE", headers },
  );
  await handleResponse(response);
}

export async function checkAdminStatus(): Promise<boolean> {
  try {
    const response = await fetch(`${baseUrl()}/skill/check-access`, {
      method: "POST",
      headers: await authHeaders(),
      body: "{}",
    });
    if (!response.ok) return false;
    const body = (await response.json()) as { isAdmin?: boolean };
    return body.isAdmin === true;
  } catch (err) {
    if (!(err instanceof GitHubProxyError && err.statusCode === 401)) {
      reportError(err, "check-admin-status");
    }
    return false;
  }
}

export async function reprocessRepo(
  request: ReprocessRequest,
): Promise<ReprocessResponse> {
  const response = await fetch(`${baseUrl()}/skill/reprocess`, {
    method: "POST",
    headers: await authHeaders(),
    body: JSON.stringify(request),
  });
  return handleResponse<ReprocessResponse>(response);
}

export { GitHubProxyError };
